import { useMemo } from "react";
import {
  computeTotals,
  type ComputeContext,
  type ComputeResult,
  type EngineLineInput,
} from "@steelbill/tax";

/**
 * The live running total (spec §11.1, §3.1).
 *
 * §11.1: "Live running total shown large at the bottom as lines are added,
 * computed client-side via `packages/tax` (§3.1) so it can never disagree with
 * the saved document."
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ARITHMETIC IS NOT HERE, AND MUST NEVER BE.
 *
 * CLAUDE.md: "Tax math lives only in packages/tax. Never compute tax inline in
 * a route handler, and never reimplement the math in the client for a 'quick
 * preview' — both surfaces import the same package." A hand-rolled
 * `subtotal * 0.18` in the editor would look right on every invoice until the
 * first one where rounding, a group header's derived rate, or an inter-state
 * customer made it disagree with the PDF the customer receives.
 *
 * This file adds exactly two things on top of `computeTotals`: memoisation, and
 * a guard so a half-typed line does not crash the editor.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * AND THE SERVER STILL DECIDES. §6: the API recomputes taxable values, taxes,
 * rounding, totals and supply type on save and IGNORES anything the client
 * sends. This total is what the owner watches while typing; it is not what gets
 * persisted. They agree because they are the same function, not because the
 * client is trusted.
 */

/** What the editor renders under the line list. */
export interface LiveTotals {
  /** The computed document, or null while the lines are not yet computable. */
  result: ComputeResult | null;
  /**
   * Why `result` is null, for the developer console only.
   *
   * NEVER SHOWN TO THE OWNER. `computeTotals` throws engineer's messages
   * ("computeTotals: duplicate line key \"10-10\""), and §11.1 requires plain
   * language. A screen with an incomputable document shows a dash where the
   * total goes and leaves the offending FIELD marked by its own zod error —
   * which is the message the owner can actually act on.
   */
  problem: string | null;
}

/**
 * Compute a document's totals, tolerating a document that is not finished.
 *
 * `computeTotals` throws — deliberately and by design (it is the authority, and
 * a silently-zero total on an invoice would be worse than an exception). But
 * this runs on EVERY KEYSTROKE in the line editor, over a document that is
 * incomplete by definition halfway through: a line with no rate yet, a
 * sub-line whose parent has not been created, a duplicate key for the moment
 * between adding a row and numbering it. Every one of those throws, and an
 * uncaught throw in a render path unmounts the editor and loses the owner's
 * typing.
 *
 * So: catch, and report "not computable yet" rather than crashing. The invalid
 * document never reaches the API — the zod schemas in `packages/types` reject
 * it at submit — and the server recomputes regardless (§6).
 */
export function safeComputeTotals(
  lines: readonly EngineLineInput[],
  context: ComputeContext | null,
): LiveTotals {
  if (context === null) {
    // No customer chosen yet, so intra- vs inter-state is unknown (§4) and
    // there is no honest tax figure to show. A total computed against a guessed
    // state would flip between CGST+SGST and IGST when the customer is picked.
    return { result: null, problem: "No customer selected — supply type unknown." };
  }

  if (lines.length === 0) return { result: null, problem: null };

  try {
    return { result: computeTotals([...lines], context), problem: null };
  } catch (error) {
    return { result: null, problem: error instanceof Error ? error.message : String(error) };
  }
}

/**
 * The hook the line editors use.
 *
 * Memoised on the lines and the two state codes. `computeTotals` is pure
 * integer arithmetic over a handful of lines, so this is not about speed — it
 * is about identity: an unmemoised `ComputeResult` is a new object every
 * render, and anything downstream keyed on it (a `useEffect` that saves a
 * draft to Dexie, for instance) would fire on every keystroke.
 */
export function useLiveTotals(
  lines: readonly EngineLineInput[],
  context: ComputeContext | null,
): LiveTotals {
  return useMemo(
    () => safeComputeTotals(lines, context),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the two scalars ARE the context
    [lines, context?.companyStateCode, context?.customerStateCode],
  );
}

/**
 * The context that reproduces a document's ALREADY-DECIDED split, given the
 * server's own answer (§17.3).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT COPIES A DECISION; IT DOES NOT MAKE ONE, and that distinction is the whole
 * justification for this function existing.
 *
 * `determineSupplyType` reads NOTHING from the two codes but whether they are
 * EQUAL (packages/tax/src/compute.ts — `company === customer`). Everywhere else
 * in this app that equality is the server's to evaluate, from the seller's GSTIN
 * and the buyer's state, and §6 forbids a screen deriving it a second time.
 *
 * A CREDIT NOTE IS THE ONE DOCUMENT WHERE THERE IS NOTHING TO DERIVE. §17.3 and
 * §8.7 require its split to be COPIED from the original invoice — a buyer who
 * has moved state since must not flip an intra-state bill's CGST+SGST into IGST,
 * or the original liability stays un-reversed in two heads while a new one is
 * created in a third. So the input here is the invoice's OWN `supplyType`, a
 * field the server computed and put on the wire, and this function's only job is
 * to express that same answer in the two-code shape the engine's signature asks
 * for. It cannot disagree with the server, because it is not asking the
 * question.
 *
 * THE "OTHER" CODE IS DELIBERATELY NOT A REAL STATE. For an inter-state
 * document the engine needs two codes that DIFFER and looks at nothing else;
 * inventing a plausible-looking neighbouring state would suggest the number
 * means something. `normaliseStateCode` validates the FORMAT only (two digits),
 * so `"00"` — which is not an assigned GST state code — satisfies it while being
 * unmistakably a placeholder to anyone who reads it in a debugger. If the place
 * of supply itself were ever `"00"`, the pair would still differ from `"99"`.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function contextForSupplyType(
  supplyType: string,
  placeOfSupply: string,
): ComputeContext {
  if (supplyType === "INTRA_STATE") {
    return { companyStateCode: placeOfSupply, customerStateCode: placeOfSupply };
  }
  return {
    companyStateCode: placeOfSupply === "00" ? "99" : "00",
    customerStateCode: placeOfSupply,
  };
}

/**
 * The lines the API takes, read as the lines the ENGINE takes.
 *
 * `toApiLine` (features/invoices/use-invoice-editor.ts) already produces every
 * field {@link EngineLineInput} declares — `key`, `parentKey`, `isGroupHeader`,
 * `pricingMode`, `gstRateBp`, `qtyMilli`, `ratePaise` and whichever measurement
 * column the pricing mode requires — plus the descriptive fields the engine
 * ignores. It is a SUPERSET, not a different shape.
 *
 * So this narrows rather than converts, and that is the point: a second
 * conversion from `EditorLine` to `EngineLineInput` would be a fourth place that
 * has to learn about a new pricing mode, and the one that got it wrong would be
 * the one the owner is watching while he types. The cast is confined to this
 * function, which is the only thing it buys.
 */
export function engineLinesFrom(apiLines: readonly Record<string, unknown>[]): EngineLineInput[] {
  return apiLines as unknown as EngineLineInput[];
}

export type { ComputeContext, ComputeResult, EngineLineInput };
